export type WebsitePreviewFramework = 'astro' | 'next';

export interface WebsitePreviewFrameworkProfile {
  id: WebsitePreviewFramework;
  displayName: string;
  nodeVersion: string;
  defaultPort: number;
  templateIdEnvironmentVariable: string;
  hostnameFlag: '--host' | '--hostname';
  pauseDevForBuild: boolean;
  taskIds: {
    install: string;
    dev: string;
    build: string;
  };
}

export const ASTRO_PREVIEW_FRAMEWORK: WebsitePreviewFrameworkProfile = {
  id: 'astro',
  displayName: 'Astro',
  nodeVersion: '22.12.0',
  defaultPort: 4321,
  templateIdEnvironmentVariable: 'CODESANDBOX_ASTRO_TEMPLATE_ID',
  hostnameFlag: '--host',
  pauseDevForBuild: false,
  taskIds: {
    install: 'attune-install',
    dev: 'attune-dev',
    build: 'attune-build',
  },
};

export const NEXT_PREVIEW_FRAMEWORK: WebsitePreviewFrameworkProfile = {
  ...ASTRO_PREVIEW_FRAMEWORK,
  id: 'next',
  displayName: 'Next.js',
  defaultPort: 3000,
  templateIdEnvironmentVariable: 'CODESANDBOX_NEXT_TEMPLATE_ID',
  hostnameFlag: '--hostname',
  pauseDevForBuild: true,
};

export function websiteFrameworkProfile(framework: WebsitePreviewFramework) {
  return framework === 'next' ? NEXT_PREVIEW_FRAMEWORK : ASTRO_PREVIEW_FRAMEWORK;
}

export function bindingWebsiteFramework(binding: {
  framework?: WebsitePreviewFramework | null;
  detected_astro?: boolean;
}): WebsitePreviewFramework | null {
  return binding.framework || (binding.detected_astro ? 'astro' : null);
}

export function detectWebsiteFramework(value: unknown): WebsitePreviewFramework | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const manifest = value as {
    dependencies?: Record<string, unknown>;
    devDependencies?: Record<string, unknown>;
  };
  const astro = Boolean(manifest.dependencies?.astro || manifest.devDependencies?.astro);
  const next = Boolean(manifest.dependencies?.next || manifest.devDependencies?.next);
  // Mixed framework packages need a distinct site root; never guess the runtime.
  return astro === next ? null : astro ? 'astro' : 'next';
}

export function bindingPreviewPort(binding: {
  framework?: WebsitePreviewFramework | null;
  detected_astro?: boolean;
  preview_port?: number;
}) {
  return (
    binding.preview_port ||
    websiteFrameworkProfile(bindingWebsiteFramework(binding) || 'astro').defaultPort
  );
}

export const WEBSITE_FRAMEWORK_REINSPECTION_REASON =
  'Repository framework changed. A project manager must inspect and save the site settings again.';

export function bindingRequiresFrameworkReinspection(binding: {
  status_detail?: string | null;
  framework?: WebsitePreviewFramework | null;
  detected_astro?: boolean;
}) {
  return (
    !bindingWebsiteFramework(binding) ||
    Boolean(binding.status_detail?.startsWith(WEBSITE_FRAMEWORK_REINSPECTION_REASON))
  );
}
